import React from "react";
import styled, { keyframes, css } from "styled-components";
import theme from "../../UI/theme";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  
`;

const Pill = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0px 150px;
  border: 2px solid ${theme.colors.greenEnergy};
  border-radius: 20px;

  /* Only the border gets 85% strength */
  @supports (color: color-mix(in srgb, red, transparent)) {
    border-color: color-mix(in srgb, ${theme.colors.greenEnergy} 60%, transparent);
  }

  @media (max-width: ${theme.screenSizes.medium}px) {
    padding: 0px 100px;
  }

  @media (max-width: ${theme.screenSizes.small}px) {
    padding: 0px 30px;
  }
`;

const EmojiWrap = styled.div`
  position: relative;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
`;

/* Glow + fade + scale + slight rotate, color-cycling between the two theme colors */
const sparkle = keyframes`
  0%   { opacity: 0; transform: scale(0.6); color: var(--cb); currentColor; }
  20%  { opacity: 1; transform: scale(1); color: var(--cb); currentColor, 0 0 12px currentColor; }
  50%  { opacity: 1; transform: scale(1.05); color: var(--ge); currentColor, 0 0 16px currentColor; }
  80%  { opacity: 0.85; transform: scale(0.9); color: var(--cb); currentColor, 0 0 10px currentColor; }
  100% { opacity: 0; transform: scale(0.6); color: var(--cb); currentColor; }
`;

/* A single sparkle (a small ✦ character) placed around the emoji */
const Star = styled.span`
  --cb: ${theme.colors.crystalBlue};
  --ge: ${theme.colors.greenEnergy};

  position: absolute;
  left: ${(p) => p.$left || 0}px;
  top: ${(p) => p.$top || 0}px;
  font-size: ${(p) => p.$size || 10}px;
  line-height: 1;
  pointer-events: none;
  opacity: 0;

  animation: ${sparkle} ${(p) => p.$duration || 2200}ms ease-in-out infinite;
  animation-delay: ${(p) => p.$delay || 0}ms;

  /* Make the glow follow the current color */
  color: var(--cb);
`;

/* Keep your original emoji size */
const Emoji = styled.div`
  font-size: 20px;
  line-height: 1;
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const opacityShimmer = keyframes`
  0%   { -webkit-mask-position: -200% 0; mask-position: -200% 0; }
  100% { -webkit-mask-position:  200% 0; mask-position:  200% 0; }
`;

const TextWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const textShimmerCss = css`
  background: linear-gradient(
    90deg,
    ${theme.colors.crystalBlue} 0%,
    ${theme.colors.greenEnergy} 30%,
    ${theme.colors.crystalBlue} 70%,
    ${theme.colors.greenEnergy} 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 21s linear infinite;
`;

/* Base: constant 0.5 opacity */
const TextBase = styled.div`
  ${textShimmerCss}
  opacity: 1;
  font-size: 13px;
`;

/* Highlight: soft band 0→1→0 moving across */
const TextDim = styled.div`
  ${textShimmerCss}
  position: absolute;
  inset: 0;
  pointer-events: none;
  font-size: 13px;
  
  filter: brightness(0.5);
  will-change: filter, -webkit-mask-position, mask-position;

  /* Soft edges (no hard line) */
  -webkit-mask-image: linear-gradient(
    90deg,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,0.12) 30%,
    rgba(0,0,0,0.35) 42%,
    rgba(0,0,0,0.75) 48%,
    rgba(0,0,0,1.0) 50%,
    rgba(0,0,0,0.75) 52%,
    rgba(0,0,0,0.35) 58%,
    rgba(0,0,0,0.12) 70%,
    rgba(0,0,0,0) 100%
  );
  -webkit-mask-size: 200% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-image: linear-gradient(
    90deg,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,0.12) 30%,
    rgba(0,0,0,0.35) 42%,
    rgba(0,0,0,0.75) 48%,
    rgba(0,0,0,1.0) 50%,
    rgba(0,0,0,0.75) 52%,
    rgba(0,0,0,0.35) 58%,
    rgba(0,0,0,0.12) 70%,
    rgba(0,0,0,0) 100%
  );
  mask-size: 200% 100%;
  mask-repeat: no-repeat;

  animation:
    ${shimmer} 21s linear infinite,
    ${opacityShimmer} 16.2s linear infinite;
`;


const AIPromptPill = ({
  customer = {}
}) => {
    const numberOfWeeks = 3;
    const createdPastFourWeeks = customer.createdOn && new Date(customer.createdOn) > Date.now() - numberOfWeeks * 7 * 24 * 60 * 60 * 1000;
  return (
    <>
    {createdPastFourWeeks &&  // need to fetch this from database - affects backend!
        <Container>
        <Pill>
            <EmojiWrap>
                {/* A few tiny stars around the emoji; each with its own timing */}
                <Star $left={5}   $top={8}  $size={7}  $delay={700}  $duration={20000}>✦</Star>
                <Star $left={8}   $top={19}  $size={9}  $delay={700}  $duration={12000}>✦</Star>
                <Star $left={13}   $top={8}  $size={14}  $delay={700}  $duration={16000}>✦</Star>
            </EmojiWrap>
            <TextWrap>
                <TextBase>Waved is learning</TextBase>
                <TextDim aria-hidden style={{ animationDirection: "normal" }}>
                    Waved is learning
                </TextDim>
                <TextDim aria-hidden style={{ animationDirection: "reverse", animationDelay: "3.6s", animationDuration: "12.9s" }}>
                    Waved is learning
                </TextDim>
                <TextDim aria-hidden style={{ animationDirection: "normal", animationDelay: "5.2s", animationDuration: "21s" }}>
                    Waved is learning
                </TextDim>
                <TextDim aria-hidden style={{ animationDirection: "reverse", animationDelay: "1.8s", animationDuration: "16.2s" }}>
                    Waved is learning
                </TextDim>
            </TextWrap>
        </Pill>
        </Container>
    }
  </>);
};

export default AIPromptPill;
